import * as Sentry from "@sentry/react";
import { captureHandledException } from "@/lib/sentry";

/**
 * Development-only logger utility
 * Logs will only appear in local development, not in staging or production builds.
 * All logger.error(...) calls forward to Sentry regardless of environment.
 */

const isDevelopment = import.meta.env.DEV;

function captureToSentry(args: any[]) {
  const error = args.find((argument) => argument instanceof Error);
  if (error !== undefined) {
    captureHandledException(error, "logger.error");
    return;
  }

  if (args.length > 1) {
    captureHandledException(args[args.length - 1], "logger.error");
    return;
  }

  // Error logs without an Error still carry a static signal, but never forward
  // the message or any arguments which could contain request or personal data.
  if (import.meta.env.VITE_SENTRY_DSN) {
    try {
      Sentry.captureMessage("Client logger.error", { level: "error" });
    } catch {
      // Logging and reporting failures must not interrupt the caller.
    }
  }
}

export const logger = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
    captureLoggedException(args, "logger.log");
  },

  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn(...args);
    }
    captureLoggedException(args, "logger.warn");
  },

  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
    captureToSentry(args);
  },

  debug: (...args: any[]) => {
    if (isDevelopment) {
      console.debug(...args);
    }
  },
};

function captureLoggedException(args: any[], operation: string) {
  const error = args.find((argument) => argument instanceof Error);
  if (error !== undefined) {
    captureHandledException(error, operation);
  }
}
